import React, { forwardRef, useState, useEffect } from "react";

export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helpText?: string;
  variant?: "default" | "outline" | "filled";
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
  showCharacterCount?: boolean;
  maxCharacters?: number;
  autoResize?: boolean;
}

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      error,
      helpText,
      variant = "default",
      size = "md",
      fullWidth = false,
      showCharacterCount = false,
      maxCharacters,
      autoResize = false,
      className = "",
      id,
      value,
      defaultValue,
      onChange,
      required,
      ...props
    },
    ref
  ) => {
    const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = error ? `${textareaId}-error` : undefined;
    const helpId = helpText ? `${textareaId}-help` : undefined;

    const [characterCount, setCharacterCount] = useState(0);

    useEffect(() => {
      const textValue = value?.toString() || defaultValue?.toString() || "";
      setCharacterCount(textValue.length);
    }, [value, defaultValue]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setCharacterCount(newValue.length);

      if (maxCharacters && newValue.length > maxCharacters) {
        return; // Prevent typing beyond max characters
      }

      if (onChange) {
        onChange(e);
      }
    };

    const baseClasses = [
      "border transition-colors duration-200 focus:outline-none focus:ring-2",
      "resize-vertical min-h-[80px] placeholder-zinc-500",
      fullWidth ? "w-full" : "",
      autoResize ? "resize-none overflow-hidden" : "",
    ];

    const sizeClasses = {
      sm: "px-2 py-1 text-sm",
      md: "px-3 py-2 text-base",
      lg: "px-4 py-3 text-lg",
    };

    const variantClasses = {
      default: error
        ? "border-red-500 bg-zinc-800 text-white focus:border-red-400 focus:ring-red-500"
        : "border-zinc-700 bg-zinc-800 text-white focus:border-amber-400 focus:ring-amber-400",
      outline: error
        ? "border-2 border-red-500 bg-zinc-900 text-white focus:border-red-400 focus:ring-red-500"
        : "border-2 border-zinc-700 bg-zinc-900 text-white focus:border-amber-400 focus:ring-amber-400",
      filled: error
        ? "border-red-500 bg-zinc-800 text-white focus:border-red-400 focus:ring-red-500"
        : "border-zinc-600 bg-zinc-800 text-white focus:border-amber-400 focus:ring-amber-400",
    };

    const textareaClasses = [
      ...baseClasses,
      sizeClasses[size],
      variantClasses[variant],
      props.disabled ? "opacity-50 cursor-not-allowed bg-zinc-800" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    const isOverLimit = maxCharacters ? characterCount > maxCharacters : false;

    return (
      <div className={fullWidth ? "w-full" : ""}>
        {label && (
          <label
            htmlFor={textareaId}
            className={`block text-sm font-medium mb-1 ${error ? "text-red-400" : "text-amber-400"}`}
          >
            {label}
            {required && <span className="text-red-400 ml-1">*</span>}
          </label>
        )}

        <textarea
          ref={ref}
          id={textareaId}
          className={textareaClasses}
          aria-invalid={error ? "true" : "false"}
          aria-describedby={[errorId, helpId].filter(Boolean).join(" ") || undefined}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          {...props}
        />

        {showCharacterCount && (
          <div className="flex justify-between items-center mt-1">
            <div className="flex-1">
              {error && (
                <p id={errorId} className="text-sm text-red-400" role="alert">
                  {error}
                </p>
              )}
              {helpText && !error && (
                <p id={helpId} className="text-sm text-zinc-400">
                  {helpText}
                </p>
              )}
            </div>
            <p className={`text-sm ml-2 ${isOverLimit ? "text-red-400" : "text-zinc-400"}`}>
              {characterCount}
              {maxCharacters && `/${maxCharacters}`}
            </p>
          </div>
        )}

        {!showCharacterCount && (
          <>
            {error && (
              <p id={errorId} className="mt-1 text-sm text-red-400" role="alert">
                {error}
              </p>
            )}

            {helpText && !error && (
              <p id={helpId} className="mt-1 text-sm text-zinc-400">
                {helpText}
              </p>
            )}
          </>
        )}
      </div>
    );
  }
);

TextArea.displayName = "TextArea";

export default TextArea;
